import { initGiftPackageStickyEffect } from "../GiftPackageStickyEffect";
import { mapTilesStyle } from "../map-utils";
import 'maplibre-gl/dist/maplibre-gl.css';
import { highlighExperienceGalleryItem } from "../Search/swiper-utils";
import { initSwiper } from "../../common/init-swiper";
import { initCopyLinkToClipboard } from "../../common/init-copy-link-to-clipboard";

class GiftPackageProduct {
  constructor(maplibregl) {
    this.wrapper = document.getElementById("gift-package-product");
    if(!this.wrapper) return;

    // init copy link to clipboard in the share url modal
    initCopyLinkToClipboard();

    // init sticky like effect
    initGiftPackageStickyEffect();

    // init list map switch
    this.initListMapSwitch();

    // init load more button
    this.initLoadMoreButton();

    this.mapIsInitialized = false;
    this.maplibregl = maplibregl;
  }

  /**
   * Init the list map switch
   */
  initListMapSwitch() {
    this.optionsList = this.wrapper.querySelector("#included-options-list");
    this.optionsMap = this.wrapper.querySelector("#included-options-map");
    this.displayModeRadios = this.wrapper.querySelectorAll("[name='display_mode']");
    if(!this.displayModeRadios || !this.optionsList || !this.optionsMap) return;

    // on click on the list map switch, we toggle the list and map containers
    this.displayModeRadios.forEach(radio => {
      radio.addEventListener("change", () => {
        if(radio.checked && radio.value === "list") {
          this.optionsList.classList.remove("hidden");
          this.optionsMap.classList.add("hidden");
        }
        else if(radio.checked && radio.value === "map") {
          if(!this.mapIsInitialized){
            this.initMap();
            this.mapIsInitialized = true;
          }

          this.optionsList.classList.add("hidden");
          this.optionsMap.classList.remove("hidden");
        }
      });
    });
  }

  /**
   * Init the load more button
   */
  initLoadMoreButton() {
    this.loadMoreButton = this.wrapper.querySelector("#load-more-button");
    this.listContainer = this.wrapper.querySelector("#list-container");
    if(!this.loadMoreButton || !this.listContainer) return;

    this.loadMoreButton.addEventListener("click", () => {
      this.loadMoreButton.classList.add("loading");
      this.loadMoreButton.disabled = true;

      // fetch the more content
      fetch(this.loadMoreButton.dataset.url + '?page=' + this.loadMoreButton.dataset.page)
        .then(response => response.text())
        .then(html => {
          if(html.trim() !== ''){
            // append the new content to the list container
            this.listContainer.innerHTML += html;

            // remove the loading class and enable the button
            this.loadMoreButton.classList.remove("loading");
            this.loadMoreButton.disabled = false;

            // check if we have more results to load
            const currentTotalResults = this.listContainer.querySelectorAll('[data-gift-package-item]').length;
            if(Number(this.loadMoreButton.dataset.countTotalResults) === currentTotalResults){
              this.loadMoreButton.remove();
            }
          }
          // no more content to load, remove the button
          else{
            this.loadMoreButton.remove();
          }
        })
        .catch(error => {
          console.error('Error loading more content:', error);
          this.loadMoreButton.classList.remove("loading");
          this.loadMoreButton.disabled = false;
        });

      // increment the page number
      this.loadMoreButton.dataset.page++;
    });
  }

  /**
   * Init the map
   */
  initMap() {
    this.mapContainer = this.wrapper.querySelector("#map-container");
    if(!this.mapContainer) return;

    window.setTimeout(() => {
      this.map = new this.maplibregl.Map({
        name: 'Winalist',
        container: this.mapContainer,
        style: mapTilesStyle,
        zoom: 8,
        dragPan: true,
        doubleClickZoom: true,
        touchZoomRotate: false
      });

      this.map.addControl(new this.maplibregl.NavigationControl({
        showCompass: false
      }));
      this.map.scrollZoom.disable();
      this.map.touchZoomRotate.disable();

      // get the markers data
      this.fetchMapData();
    }, 500);
  }

  /**
   * Fetch the map data
   */
  fetchMapData() {
    this.markers = [];

    fetch(this.mapContainer.dataset.url)
      .then(response => response.json())
      .then(data => {
        // add markers to the map
        this.createMarkers(data.data);

        // then place the map accordingly to fit the markers at best
        this.fitMarkersToMapBounds();
      });
  }

  /**
   * Create the markers
   */
  createMarkers(results) {
    if(results.length === 0) return;

    // Group results by coordinates
    const groupedResults = this.groupResultsByCoordinates(results);

    // Create markers for each coordinate group
    groupedResults.forEach(group => {
      const elMarker = document.createElement('div');
      elMarker.className = 'marker single-marker';

      const marker = new this.maplibregl.Marker({
        element: elMarker,
      });

      marker.setLngLat([group.lng, group.lat]);
      marker.setPopup(this.createPopup(group.results));
      marker.addTo(this.map);

      this.markers.push(marker);
    });
  }

  /**
   * Group results by their coordinates
   */
  groupResultsByCoordinates(results) {
    const groups = new Map();

    results.forEach(result => {
      const key = `${result.lat},${result.lng}`;

      if (!groups.has(key)) {
        groups.set(key, {
          lat: result.lat,
          lng: result.lng,
          results: []
        });
      }

      groups.get(key).results.push(result);
    });

    return Array.from(groups.values());
  }

  createPopup(results){
    const popupOptions = {
      offset: 15,
      closeButton: false,
    };

    let popupContent = '';
    if(this.mapContainer.dataset.resultsType === 'experience'){
      popupContent += this.buildExperiencesSliderPopup(results);
    }
    else{
      popupContent += this.buildHostsSliderPopup(results);
    }

    let popup = new this.maplibregl.Popup(popupOptions).setHTML(popupContent);
    popup.on("open", () => {
      const popupContent = popup.getElement();

      //force active on the first experience mini gallery picture
      highlighExperienceGalleryItem(popupContent, null, true);

      //init any swiper slider
      const swiper = initSwiper(popupContent, {
        callbacks: {
          slideChangeTransitionEnd: (swiper) => {
            //when a slide is changed, highlight the experience mini gallery picture
            highlighExperienceGalleryItem(popupContent, swiper);
          }
        }
      });

      // just to be sure, slide to the first slide
      if(swiper) swiper.update();
    });

    return popup;
  }

    /**
   * Builds the experiences slider popup (for desktop only).
   *
   * @param {type} results
   * @return {string} The HTML for the experiences slider popup.
   */
    buildExperiencesSliderPopup(results){
      let html = `<article class="card card-host experiences-slider-desktop on-popup bg-transparent max-w-xs w-64 sm:w-80 overflow-hidden text-left">
      <div class="swiper w-full h-full rounded-lg overflow-hidden"
            data-per-view="1"
            data-per-view-xs="1"
            data-per-view-sm="1"
            data-per-view-lg="1"
            data-per-view-xl="1"
            data-per-view-wide-xl="1"
            data-per-view-ultrawide-xl="1">
        <div class="swiper-wrapper h-full w-full">`;

      const experiences = results;
      if(typeof results !== 'undefined' && results.length > 0){
        results.forEach(result => {
          const experience = result;
          const host = result.host;

          let byTrad = this.mapContainer.dataset.by.replace("{0}", host.name);

          let src = experience.coverCdnUrl + '/resize=2p';

          html += `
          <div class="swiper-slide w-full h-full">
            <div class="flex flex-col rounded" data-experience-id="${experience.id}">
              <a href="${experience.url}" target="_blank" class="relative block h-40 sm:h-48 w-full bg-transparent rounded-t-lg overflow-hidden img-blur-container">
                <img src="${src}" data-src="${experience.coverCdnPath}" class="twic h-full w-full object-cover" loading="lazy" />
                <div class="swiper-lazy-preloader"></div>
              </a>
              <div class="p-4 flex flex-col">
                <div class="flex flex-wrap items-center">
                  <span class="badge badge-champagnelight text-gray-700 mr-2 text-tiny sm:text-xs">
                    ${experience.activity_name}
                  </span>`;

          if(experience.count_reviews > 0){
            html += `
                    <div class="star-rating mr-1" title="${experience.average_review}">
                      <div class="back-stars">
                          <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                          <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                          <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                          <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                          <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                          <div class="front-stars" style="width: ${experience.average_review * 20}%">
                              <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                              <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                              <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                              <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                              <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                          </div>
                      </div>
                    </div>
                    <span class="text-xs mr-3">(${experience.count_reviews})</span>`;
          }

          html += `
                </div>
                <a href="${experience.url}" target="_blank" class="mt-2 font-semibold text-lg text-black hover:no-underline">
                  ${experience.name}
                </a>
                <a href="${host.url}" class="text-sm relative z-50 mt-1">${byTrad}</a>
              </div>
            </div>
          </div>
        `;
        });
      }


      //if host has more than one experience, setup the gallery pictures
      if(typeof experiences !== 'undefined' && experiences.length > 1){
        html += `
          </div>

          <button type="button"
                  class="swiper-prev left-0 ml-2 vertically-centered p-4 sm:p-2 z-20 hidden lg:flex items-center justify-center shadow-lg absolute rounded-full border border-gray-500 bg-white">
              <i class="icon icon-caret-left text-xl"></i>
          </button>

          <button type="button"
                  class="swiper-next right-0 mr-2 vertically-centered p-4 sm:p-2 z-20 hidden lg:flex items-center justify-center shadow-lg absolute rounded-full border border-gray-500 bg-white">
              <i class="icon icon-caret-right text-xl"></i>
          </button>
        </div>`;

        html += `<div class="gallery flex ${experiences.length > 5 ? 'flex-wrap' : ''} bg-white">`;

        let width = "16.6%";
        if(experiences.length <= 6){
            width = 100 / experiences.length + "%";
        }

        experiences.forEach((experience, index) => {
          let src = experience.coverCdnUrl + '/resize=2p';

          html += `
            <button type="button" class="gallery-item p-2 outline-none border-t-2 ${index == 0 ? 'active' : ''} flex justify-center bg-white"
                    style="width:${width}" data-index="${index}">
                <img class="h-8 w-8 rounded-lg object-cover" alt="${experience.name}"
                      draggable="false" src="${src}/cover=70x70" loading="lazy" />
            </button>
          `;
        });

        html += `</div>`;
      }

      html += `
      </article>`;

      return html;
    }

  /**
   * Builds the experiences slider popup (for desktop only).
   *
   * @param {type} host - The host parameter description.
   * @return {string} The HTML for the experiences slider popup.
   */
    buildHostsSliderPopup(results){
      let html = `<article class="card card-host experiences-slider-desktop on-popup bg-transparent max-w-xs w-64 sm:w-80 overflow-hidden text-left">`;

      const host = results[0];
      if(typeof host !== 'undefined'){
        let src = host.mediaCdnUrl + '/resize=2p';

        html += `
          <div class="flex flex-col rounded-lg">
            <a href="${host.url}" target="_blank" class="relative block h-40 sm:h-48 w-full bg-transparent rounded-t-lg overflow-hidden img-blur-container">
              <img src="${src}" data-src="${host.mediaCdnPath}" class="twic h-full w-full object-cover" loading="lazy" />
            </a>
            <div class="p-4 flex flex-col">
              <div class="flex flex-wrap items-center">`;

        if(host.count_reviews > 0){
          html += `
                  <div class="star-rating mr-1 mb-2" title="${host.average_review}">
                    <div class="back-stars">
                        <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                        <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                        <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                        <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                        <i class="icon icon-star-alt text-gray-300" aria-hidden="true"></i>
                        <div class="front-stars" style="width: ${host.average_review * 20}%">
                            <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                            <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                            <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                            <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                            <i class="icon icon-star-alt text-yellow" aria-hidden="true"></i>
                        </div>
                    </div>
                  </div>
                  <span class="text-xs mr-3 mb-1">(${host.count_reviews})</span>`;
        }

        html += `
              </div>
              <a href="${host.url}" target="_blank" class="font-semibold text-lg text-black hover:no-underline">
                ${host.name}
              </a>
            </div>
          </div>
      `;
      }

      html += `
      </article>`;

      return html;
    }

  /**
   * Fit the markers to the map bounds
   */
  fitMarkersToMapBounds(){
    if(this.markers.length === 0) return;

    const coordinates = this.markers.map(marker => marker.getLngLat());

    // create a bounds object
    const bounds = new this.maplibregl.LngLatBounds();

    // extend the bounds with each coordinate
    coordinates.forEach(coord => bounds.extend(coord));

    // adjust the map view to fit all the markers
    this.map.fitBounds(bounds, {
      padding: 100,   // margins around the markers
      maxZoom: 14,   // limit if points are too close
      duration: 500 // animation in ms
    });
  }
}

export default GiftPackageProduct;
